import React from 'react'
import { Stack, Typography, useTheme, Skeleton } from '@mui/material'
import { styled } from '@mui/system'

interface StyledWidgetContainerProps {
  disabled?: boolean
}

const StyledWidgetContainer = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'disabled'
})<StyledWidgetContainerProps>(({ theme, disabled }) => ({
  border: `1px solid ${theme.palette.grey[200]}`,
  borderRadius: '24px',
  width: '100%',
  padding: theme.spacing(2, 3),
  cursor: disabled ? 'default' : 'pointer',
  transition: 'all 0.2s ease-in-out',
  ...(!disabled && {
    '&:hover': {
      boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)',
      borderColor: theme.palette.grey[300],
    },
  }),
}))

const ContentContainer = styled(Stack)(() => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
}))

const InfoSection = styled(Stack)(() => ({
  flex: 1,
}))

interface WidgetSingleLineInformationProps {
  value: number
  mainLabel: string
  labelInfo: string
  loading?: boolean
  onClick?: () => void
  hideArrowIcon?: boolean
  disabled?: boolean
}

const WidgetSingleLineInformation: React.FC<WidgetSingleLineInformationProps> = ({
  value,
  mainLabel,
  labelInfo,
  loading = false,
  onClick,
  disabled = false
}) => {
  const theme = useTheme()

  const formattedValue = new Intl.NumberFormat('fr-FR', { useGrouping: true }).format(value)

  if (loading) {
    return (
      <StyledWidgetContainer>
        <ContentContainer>
          <InfoSection>
            <Skeleton width="60%" height={20} />
            <Skeleton width="40%" height={16} sx={{ mt: 0.5 }} />
          </InfoSection>
          <Skeleton variant="circular" width={32} height={32} />
        </ContentContainer>
      </StyledWidgetContainer>
    )
  }

  return (
    <StyledWidgetContainer 
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
    >
      <ContentContainer>
        <InfoSection>
          <Typography fontSize={14} fontWeight={400}>
            {mainLabel}
          </Typography>
          <Typography fontSize={10} color={theme.palette.grey[600]}>
            {labelInfo}
          </Typography>
        </InfoSection>
        <Typography fontSize={22} lineHeight={1}>
          {formattedValue}
        </Typography>
      </ContentContainer>
    </StyledWidgetContainer>
  )
}

export default WidgetSingleLineInformation